import { useMemo } from 'react'
import { Loader2, Info, Check, AlertTriangle } from 'lucide-react'
import {
  comparePackages, packageUsed, packageN, packageState,
  promoName, promoMaintype, promoSubtype, stepPriceLabel,
} from '../../lib/patientPromotion.js'
import { buildSlotMap, toggleSlot, slotPrice } from '../../lib/promotionSlots.js'

/**
 * The โปรโมชั่น mode of the billing drawer — pick which package to bill, and WHICH
 * INSTALLMENTS of it. PRESENTATIONAL ONLY: the parent owns loading, the selection
 * and the write, because it already holds `types` and the treatment context and a
 * second copy of either would be a second source of truth about what a line costs.
 *
 * ⚠️ NOTHING HERE IS EDITABLE, and that is the point. A promotion installment is
 * priced by its own pattern step — ฿0 included, which means free — and every figure
 * is recomputed inside Xano at the moment of the write. An input on this panel would
 * be a fourth place that decides what a package costs. Corrections go through ปกติ
 * mode AFTER the line exists, where they are logged.
 *
 * ## Two grades of picking, and why the weaker one still ships
 *
 * `rows` is the patient's own active billing rows, split by package — the ledger
 * this drawer was opened over already holds them. When a package's rows can be
 * matched to its pattern steps (`map.known`), every position is addressable: tick
 * any free installment, in any order, including one a voided line left behind.
 *
 * When they cannot — and promotionSlots.js lists every reason — the panel falls
 * back to what api 189 can actually honour: installments are billed from the next
 * one FORWARD, so clicking a position selects the run up to it rather than that
 * position alone. It says so on screen rather than pretending. That fallback is
 * today's live behaviour, extended only by "more than one at a time", which
 * repeated calls to api 189 give correctly (each one re-counts).
 */
const CARD = 'w-full text-left rounded-xl border p-3 transition'

// Why a package cannot be addressed. Every one of these ends the same way, so the
// consequence is written once, below.
const REASON = {
  no_rows: 'ยังไม่มีข้อมูลรายการ Billing ของคนไข้',
  truncated: 'ประวัติการรักษาของคนไข้ยาวเกินกว่าที่ระบบโหลดได้ครบ',
  bad_pattern: 'รูปแบบการเก็บเงินของโปรโมชั่นนี้ตั้งค่าไม่ถูกต้อง (ลำดับครั้งซ้ำหรือย้อนกลับ)',
  count_mismatch: 'จำนวนครั้งที่ใช้ในระบบไม่ตรงกับรายการ Billing ที่พบ',
  unmatched_row: 'มีรายการ Billing ที่ไม่ตรงกับขั้นตอนใดของโปรโมชั่น (อาจมีการแก้รูปแบบโปรโมชั่นภายหลัง)',
  ambiguous_type: 'โปรโมชั่นนี้มีหลายขั้นตอนที่ใช้รายการเดียวกันแต่ราคาต่างกัน ระบบจึงแยกไม่ได้ว่ารายการไหนคือครั้งไหน',
  over_capacity: 'มีรายการ Billing มากกว่าจำนวนครั้งที่โปรโมชั่นกำหนด — ตรวจสอบกับผู้จัดการ',
}

export default function PromotionRedeemPanel({ packages, types, rows, complete = true, value, onChange }) {
  const typeName = useMemo(() => {
    const m = new Map()
    for (const t of types || []) m.set(Number(t.id), t.name || t.Name || '')
    return (id) => m.get(Number(id)) || (id ? `ประเภท #${id}` : '—')
  }, [types])

  // One map per package. `rows` is a Map keyed by promotion_patient_id; a package
  // with no entry gets `null`, NOT [] — "no rows loaded" and "no rows exist" are
  // different statements and only the second one may free a slot.
  const maps = useMemo(() => {
    const m = new Map()
    for (const r of packages || []) {
      m.set(r.id, buildSlotMap(r, rows?.get?.(Number(r.id)) ?? (rows ? [] : null), { complete }))
    }
    return m
  }, [packages, rows, complete])

  const sorted = useMemo(() => [...(packages || [])].sort(comparePackages), [packages])
  // Usable = has a free position. In the addressed world that is NOT the same as
  // count < max: a package can be "full" by count and still have a hole a void left
  // behind, which is exactly the case this feature exists to bill.
  const usable = sorted.filter((r) => (maps.get(r.id)?.free.length || 0) > 0)
  const blocked = sorted.filter((r) => !((maps.get(r.id)?.free.length || 0) > 0))

  if (packages === null) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-slate-400 py-8 justify-center">
        <Loader2 size={14} className="animate-spin" /> กำลังโหลดโปรโมชั่น…
      </div>
    )
  }

  if (!usable.length && !blocked.length) {
    return <div className="text-[13px] text-slate-400 text-center py-10">คนไข้ยังไม่มีโปรโมชั่น</div>
  }

  const pick = (row, map) => {
    if (value?.id === row.id) return onChange(null)
    // Opening a package pre-selects the lowest free position — after a void that is
    // the hole, not the end of the list, which is the default Ram asked for.
    onChange(map.defaultPick ? { id: row.id, pkg: row, slots: [map.defaultPick] } : null)
  }

  const clickSlot = (row, map, ordinal) => {
    const cur = value?.id === row.id ? value.slots : []
    if (map.known) return onChange({ id: row.id, pkg: row, slots: toggleSlot(cur, ordinal, map) })
    // Forward-only: api 189 bills from the next one onward whatever we ask, so a
    // click means "up to here". Clicking the current end of the run shortens it.
    const i = map.free.indexOf(ordinal)
    if (i < 0) return
    const run = cur.length === i + 1 ? map.free.slice(0, i) : map.free.slice(0, i + 1)
    onChange({ id: row.id, pkg: row, slots: run })
  }

  return (
    <div className="space-y-2.5">
      {usable.map((r) => {
        const map = maps.get(r.id)
        const on = value?.id === r.id
        const next = map.slots[map.defaultPick - 1]
        return (
          <div key={r.id}
            className={`${CARD} ${on ? 'border-brand-400 bg-brand-50/50 ring-4 ring-brand-500/10' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
            <button type="button" onClick={() => pick(r, map)} className="w-full text-left">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium text-slate-900 truncate">{promoName(r)}</div>
                  <div className="text-[12px] text-slate-400 truncate">
                    {[promoMaintype(r), promoSubtype(r)].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <div className="flex-none text-[12.5px] tabular-nums text-slate-500">
                  {packageN(r) ? `${packageUsed(r)}/${packageN(r)}` : `${packageUsed(r)} ครั้ง`}
                </div>
              </div>
            </button>

            {on && (
              <SlotPicker
                map={map}
                selected={value.slots}
                typeName={typeName}
                onSlot={(ordinal) => clickSlot(r, map, ordinal)}
              />
            )}

            {!on && next && (
              <div className="mt-2 pt-2 border-t border-slate-100 text-[12.5px] text-slate-600">
                <span className="text-slate-400">
                  {map.known ? `ครั้งที่ว่างถัดไป (${map.defaultPick}):` : `ครั้งถัดไป (${map.defaultPick}):`}
                </span>{' '}
                {typeName(next.billing_type_id)} · {stepPriceLabel(slotPrice(next))}
              </div>
            )}
          </div>
        )
      })}

      {blocked.length > 0 && (
        <div className="pt-1 space-y-2">
          <div className="text-[11.5px] uppercase tracking-wide text-slate-400">ใช้ไม่ได้</div>
          {blocked.map((r) => (
            <div key={r.id} className={`${CARD} border-slate-100 bg-slate-50/60`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] text-slate-500 truncate">{promoName(r)}</div>
                  <div className="text-[12px] text-slate-400">{blockedReason(r)}</div>
                </div>
                <div className="flex-none text-[12.5px] tabular-nums text-slate-400">
                  {packageN(r) ? `${packageUsed(r)}/${packageN(r)}` : `${packageUsed(r)} ครั้ง`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-1.5 text-[12.5px] text-slate-500 pt-1">
        <Info size={13} className="flex-none mt-0.5 text-slate-400" />
        <span>
          ราคา ส่วนลด และ DF ของรายการนี้มาจากโปรโมชั่นโดยตรง แก้ไขที่นี่ไม่ได้ —
          ถ้าต้องแก้ ให้แก้ที่รายการ Billing หลังจากเพิ่มแล้ว
        </span>
      </div>
    </div>
  )
}

/**
 * The installment list. Every position is shown, spent ones included — a hole is
 * only legible next to the lines around it, and "why is ครั้งที่ 1 free when they
 * have had three visits" is a question the grid should answer on sight.
 */
function SlotPicker({ map, selected, typeName, onSlot }) {
  const sel = new Set(selected || [])
  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-100">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12px] font-medium text-slate-500">
          {map.known ? 'เลือกครั้งที่ต้องการเก็บเงิน' : 'จำนวนครั้งที่จะเก็บเงิน'}
        </span>
        <span className="text-[11.5px] text-slate-400 tabular-nums">
          ว่าง {map.free.length} จาก {map.max}
        </span>
      </div>

      <div className="max-h-[248px] overflow-y-auto -mx-0.5 px-0.5 space-y-1">
        {map.slots.map((s) => {
          const free = s.state === 'free'
          const picked = sel.has(s.ordinal)
          return (
            <button
              key={s.ordinal}
              type="button"
              disabled={!free}
              onClick={() => onSlot(s.ordinal)}
              className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition ${
                picked
                  ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500/15'
                  : free
                    ? 'border-slate-200 bg-white hover:border-slate-300'
                    : 'border-slate-100 bg-slate-50/70 cursor-not-allowed'
              }`}
            >
              <span className={`flex-none w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center ${
                picked ? 'bg-brand-600 border-brand-600 text-white'
                       : free ? 'border-slate-300 bg-white'
                              : 'border-slate-200 bg-white text-slate-300'}`}>
                {picked && <Check size={12} />}
                {!picked && !free && <Check size={12} className="text-slate-300" />}
              </span>
              <span className={`flex-none text-[12.5px] tabular-nums ${free ? 'text-slate-500' : 'text-slate-400'}`}>
                {s.ordinal}
              </span>
              <span className={`flex-1 min-w-0 truncate text-[13px] ${free ? 'text-slate-800' : 'text-slate-400'}`}>
                {typeName(s.billing_type_id)}
              </span>
              <span className={`flex-none text-[12.5px] tabular-nums ${free ? 'text-slate-600' : 'text-slate-400'}`}>
                {free ? stepPriceLabel(slotPrice(s)) : 'ใช้แล้ว'}
              </span>
            </button>
          )
        })}
      </div>

      {/* The caveat belongs where the number is, not in a doc nobody at the counter
          reads. Stated as CAUSE then consequence, because the consequence alone
          ("เลือกครั้งที่ถูกลบไม่ได้") reads as a bug rather than a limit. */}
      {!map.known && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12px] text-amber-800 flex items-start gap-1.5">
          <AlertTriangle size={12} className="flex-none mt-0.5" />
          <span>
            {REASON[map.reason] || 'ระบบระบุไม่ได้ว่าใช้ครั้งไหนไปแล้ว'} —
            จึงถือว่าใช้เรียงจากครั้งที่ 1 และเก็บเงินได้จากครั้งถัดไปเรียงไปข้างหน้าเท่านั้น
            ครั้งที่ถูกลบไปจะยังขึ้นว่าใช้แล้ว
          </span>
        </div>
      )}
    </div>
  )
}

// Why a package cannot be redeemed at all. `dead` and `over` are catalogue faults the
// front desk must be able to report, not rows to quietly drop.
function blockedReason(row) {
  switch (packageState(row)) {
    case 'done': return 'ใช้ครบแล้ว'
    case 'over': return 'ใช้เกินสิทธิ์ที่ตั้งไว้ — ตรวจสอบกับผู้จัดการ'
    case 'dead': return 'โปรโมชั่นนี้ตั้งค่าไม่ถูกต้อง เรียกเก็บไม่ได้ — แจ้งผู้จัดการ'
    default:     return 'ใช้ไม่ได้'
  }
}
